import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Company } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Label } from "../components/ui/label.jsx";
import { Card, CardContent } from "../components/ui/card.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { ArrowLeft, Plus, X } from 'lucide-react';

export default function NewCompany() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [company, setCompany] = useState({
    corporate_name: '',
    trade_name: '',
    cnpj: '',
    status: 'active',
    address: {
      street: '',
      number: '',
      complement: '',
      district: '',
      city: '',
      state: '',
      zip_code: ''
    },
    contacts: [],
    remote_access: [],
    contracted_services: [],
    sla_config: {
      support: {
        low: 48,
        medium: 24,
        high: 8,
        critical: 4
      },
      development: {
        low: 72,
        medium: 48,
        high: 24,
        critical: 12
      }
    }
  });

  // Estado temporário para novo contato
  const [newContact, setNewContact] = useState({
    name: '',
    role: '',
    email: '',
    phone: ''
  });

  // Estado temporário para novo acesso remoto
  const [newRemoteAccess, setNewRemoteAccess] = useState({
    type: '',
    identifier: '',
    password: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await Company.create(company);
      navigate(createPageUrl('Companies'));
    } catch (error) {
      console.error('Error creating company:', error);
    }
    setIsLoading(false);
  };

  const addContact = () => {
    if (newContact.name && newContact.email) {
      setCompany({
        ...company,
        contacts: [...company.contacts, { ...newContact }]
      });
      setNewContact({ name: '', role: '', email: '', phone: '' });
    }
  };

  const removeContact = (index) => {
    setCompany({
      ...company,
      contacts: company.contacts.filter((_, i) => i !== index)
    });
  };

  const addRemoteAccess = () => {
    if (newRemoteAccess.type && newRemoteAccess.identifier) {
      setCompany({
        ...company,
        remote_access: [...company.remote_access, { ...newRemoteAccess }]
      });
      setNewRemoteAccess({ type: '', identifier: '', password: '', notes: '' });
    }
  };

  const removeRemoteAccess = (index) => {
    setCompany({
      ...company,
      remote_access: company.remote_access.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Companies'))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Nova Empresa</h1>
          <p className="text-muted-foreground mt-1">
            Cadastre uma nova empresa no sistema
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Dados Principais */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Dados da Empresa</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="corporate_name">Razão Social</Label>
                  <Input
                    id="corporate_name"
                    value={company.corporate_name}
                    onChange={(e) => setCompany({ ...company, corporate_name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trade_name">Nome Fantasia</Label>
                  <Input
                    id="trade_name"
                    value={company.trade_name}
                    onChange={(e) => setCompany({ ...company, trade_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <Input
                    id="cnpj"
                    value={company.cnpj}
                    onChange={(e) => setCompany({ ...company, cnpj: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={company.status}
                    onValueChange={(value) => setCompany({ ...company, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="inactive">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Endereço</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="street">Rua</Label>
                  <Input
                    id="street"
                    value={company.address.street}
                    onChange={(e) => setCompany({
                      ...company,
                      address: { ...company.address, street: e.target.value }
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="number">Número</Label>
                  <Input
                    id="number"
                    value={company.address.number}
                    onChange={(e) => setCompany({
                      ...company,
                      address: { ...company.address, number: e.target.value }
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="complement">Complemento</Label>
                  <Input
                    id="complement"
                    value={company.address.complement}
                    onChange={(e) => setCompany({
                      ...company,
                      address: { ...company.address, complement: e.target.value }
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="district">Bairro</Label>
                  <Input
                    id="district"
                    value={company.address.district}
                    onChange={(e) => setCompany({
                      ...company,
                      address: { ...company.address, district: e.target.value }
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">Cidade</Label>
                  <Input
                    id="city"
                    value={company.address.city}
                    onChange={(e) => setCompany({
                      ...company,
                      address: { ...company.address, city: e.target.value }
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">Estado</Label>
                  <Input
                    id="state"
                    value={company.address.state}
                    onChange={(e) => setCompany({
                      ...company,
                      address: { ...company.address, state: e.target.value }
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zip_code">CEP</Label>
                  <Input
                    id="zip_code"
                    value={company.address.zip_code}
                    onChange={(e) => setCompany({
                      ...company,
                      address: { ...company.address, zip_code: e.target.value }
                    })}
                  />
                </div>
              </div>
            </div>

            {/* Contatos */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Contatos</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={newContact.name}
                    onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Cargo</Label>
                  <Input
                    value={newContact.role}
                    onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>E-mail</Label>
                  <Input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={newContact.phone}
                    onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  />
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={addContact}
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Contato
              </Button>

              {company.contacts.length > 0 && (
                <div className="mt-4 space-y-2">
                  {company.contacts.map((contact, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{contact.name}</p>
                        <p className="text-sm text-gray-500">
                          {contact.role} • {contact.email} • {contact.phone}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeContact(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Acessos Remotos */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Acessos Remotos</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={newRemoteAccess.type}
                    onValueChange={(value) => setNewRemoteAccess({ ...newRemoteAccess, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teamviewer">TeamViewer</SelectItem>
                      <SelectItem value="anydesk">AnyDesk</SelectItem>
                      <SelectItem value="rdp">RDP</SelectItem>
                      <SelectItem value="vpn">VPN</SelectItem>
                      <SelectItem value="ssh">SSH</SelectItem>
                      <SelectItem value="other">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Identificador</Label>
                  <Input
                    value={newRemoteAccess.identifier}
                    onChange={(e) => setNewRemoteAccess({ ...newRemoteAccess, identifier: e.target.value })}
                    placeholder="ID ou endereço de acesso"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Senha</Label>
                  <Input
                    type="password"
                    value={newRemoteAccess.password}
                    onChange={(e) => setNewRemoteAccess({ ...newRemoteAccess, password: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Input
                    value={newRemoteAccess.notes}
                    onChange={(e) => setNewRemoteAccess({ ...newRemoteAccess, notes: e.target.value })}
                  />
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={addRemoteAccess}
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Acesso Remoto
              </Button>

              {company.remote_access.length > 0 && (
                <div className="mt-4 space-y-2">
                  {company.remote_access.map((access, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">{access.type.toUpperCase()}</p>
                        <p className="text-sm text-gray-500">
                          ID: {access.identifier}
                          {access.notes && ` • ${access.notes}`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRemoteAccess(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(createPageUrl('Companies'))}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Salvando...' : 'Salvar Empresa'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}